import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { ArrowLeft, Inbox, Loader2, Lock, LogOut, Mail, Phone } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TOKEN_KEY = "tosiasi_admin_token";

const TYPE_META = {
  nok_chhonk_b2b: { label: "Nok Chhonk B2B Supply", cls: "border-amber-400/40 bg-amber-400/10 text-amber-300" },
  mozekaql_it: { label: "Mozekaql IT Solutions", cls: "border-cyan-400/40 bg-cyan-400/10 text-cyan-300" },
  general_partnership: { label: "General Partnership", cls: "border-white/25 bg-white/5 text-slate-200" },
};

function formatApiErrorDetail(detail) {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).filter(Boolean).join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}

function EnquiryCard({ e, index }) {
  const meta = TYPE_META[e.inquiry_type] || TYPE_META.general_partnership;
  return (
    <div className="glass card-hover rounded-3xl p-7" data-testid={`admin-enquiry-card-${index}`}>
      <div className="flex flex-wrap items-center justify-between gap-3">
        <span className={`rounded-full border px-3 py-1 font-mono text-[10px] uppercase tracking-[0.16em] ${meta.cls}`}>
          {meta.label}
        </span>
        <span className="font-mono text-[10px] uppercase tracking-[0.18em] text-slate-500">
          {new Date(e.created_at).toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" })}
        </span>
      </div>
      <p className="mt-4 font-display text-xl font-semibold text-white">{e.name}</p>
      <div className="mt-3 flex flex-wrap gap-x-6 gap-y-2 text-sm">
        <a
          href={`mailto:${e.email}`}
          data-testid={`admin-reply-${index}`}
          className="inline-flex items-center gap-2 text-slate-300 transition-colors hover:text-amber-400"
        >
          <Mail size={14} /> {e.email}
        </a>
        <span className="inline-flex items-center gap-2 text-slate-300">
          <Phone size={14} /> {e.phone}
        </span>
      </div>
      {e.message && (
        <p className="mt-4 rounded-2xl border border-white/5 bg-white/[0.03] p-4 text-sm leading-relaxed text-slate-400">
          {e.message}
        </p>
      )}
    </div>
  );
}

export default function Admin() {
  const [token, setToken] = useState(() => sessionStorage.getItem(TOKEN_KEY));
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [enquiries, setEnquiries] = useState(null);

  const logout = useCallback(() => {
    sessionStorage.removeItem(TOKEN_KEY);
    setToken(null);
    setEnquiries(null);
  }, []);

  const load = useCallback(
    async (t) => {
      try {
        const { data } = await axios.get(`${API}/admin/enquiries`, {
          headers: { Authorization: `Bearer ${t}` },
        });
        setEnquiries(data.enquiries || []);
      } catch (err) {
        if (err?.response?.status === 401) {
          logout();
          return;
        }
        toast.error("Couldn't load enquiries", {
          description: formatApiErrorDetail(err?.response?.data?.detail),
        });
        setEnquiries([]);
      }
    },
    [logout]
  );

  useEffect(() => {
    if (token) load(token);
  }, [token, load]);

  const login = async (e) => {
    e.preventDefault();
    if (busy || !password) return;
    setBusy(true);
    try {
      const { data } = await axios.post(`${API}/admin/login`, { password });
      sessionStorage.setItem(TOKEN_KEY, data.token);
      setPassword("");
      setToken(data.token);
      toast.success("Welcome back");
    } catch (err) {
      toast.error("Login failed", { description: formatApiErrorDetail(err?.response?.data?.detail) });
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="relative min-h-screen pt-32 pb-24">
      <div className="glow-amber pointer-events-none absolute -left-40 top-0 h-[420px] w-[420px] opacity-60" />
      <div className="relative mx-auto max-w-4xl px-6">
        <Link
          to="/"
          data-testid="admin-back-link"
          className="inline-flex items-center gap-2 text-xs text-slate-400 transition-colors hover:text-white"
        >
          <ArrowLeft size={13} /> Back to site
        </Link>

        {!token ? (
          <form onSubmit={login} data-testid="admin-login-form" className="glass mx-auto mt-10 max-w-md rounded-3xl p-10">
            <span className="flex h-12 w-12 items-center justify-center rounded-2xl border border-amber-400/30 bg-amber-400/10">
              <Lock size={20} className="text-amber-400" />
            </span>
            <h1 className="mt-6 font-display text-2xl font-semibold text-white">Enquiries inbox</h1>
            <p className="mt-2 text-sm text-slate-400">Admin access only — enter the admin password.</p>
            <input
              type="password"
              data-testid="admin-password-input"
              className="field mt-6"
              placeholder="Admin password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              autoFocus
            />
            <button
              type="submit"
              data-testid="admin-login-button"
              disabled={busy}
              className="btn btn-amber mt-5 w-full disabled:cursor-not-allowed disabled:opacity-60"
            >
              {busy ? (
                <>
                  <Loader2 size={16} className="animate-spin" /> Checking…
                </>
              ) : (
                <>Unlock inbox</>
              )}
            </button>
          </form>
        ) : (
          <div className="mt-8">
            <div className="flex flex-wrap items-center justify-between gap-4">
              <h1 className="flex items-center gap-3 font-display text-2xl font-semibold text-white md:text-3xl">
                <Inbox size={24} className="text-amber-400" />
                Enquiries
                {enquiries && (
                  <span data-testid="admin-enquiry-count" className="font-mono text-xs tracking-[0.2em] text-slate-500">
                    {enquiries.length} TOTAL
                  </span>
                )}
              </h1>
              <button
                type="button"
                data-testid="admin-logout-button"
                onClick={logout}
                className="btn btn-outline-white !px-5 !py-2.5 text-xs"
              >
                <LogOut size={14} /> Log out
              </button>
            </div>
            <div className="mt-8 space-y-5" data-testid="admin-enquiries-list">
              {enquiries === null && (
                <div className="glass flex items-center justify-center gap-3 rounded-3xl p-12 text-slate-400">
                  <Loader2 size={18} className="animate-spin" /> Loading enquiries…
                </div>
              )}
              {enquiries && enquiries.length === 0 && (
                <div className="glass rounded-3xl p-12 text-center text-slate-400">
                  No enquiries yet — they&apos;ll appear here the moment the contact form is used.
                </div>
              )}
              {enquiries && enquiries.map((e, i) => <EnquiryCard key={e.id || i} e={e} index={i} />)}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
